import { FacebookFilled, InstagramFilled, TikTokFilled } from '@ant-design/icons'
import { Alert, Button, Form } from 'antd'
import dayjs from 'dayjs'
import { type FC, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import errorRegisterImg from 'url:../../public/error-register.svg'
import invalidLinkImg from 'url:../../public/invalid-link.svg'
import successRegisterImg from 'url:../../public/success-register.svg'
import {
    BusinessKindOptions,
    JobPositionOptions,
    type RegisterRes,
    type ResProfileCheck,
    type SubmissionReqBodyType,
    SubmissionReqBodyValidator,
    apiHandler,
    apiRoutes,
    appErrorMapping,
    appStatus,
    errorMessage,
} from '../apiDoc'
import {
    CheckBoxGroupUi,
    CheckBoxUi,
    DatePickerUi,
    InputUi,
    SelectUi,
} from '../components/ElementsUi'
import { MainLayout } from '../components/MainLayout'

const RegisterForm: FC<{
    formId: string
    setStatus: (e: appStatus) => void
}> = ({ formId, setStatus }) => {
    const [form] = Form.useForm()
    const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false)
    const [errorMsg, setErrMsg] = useState<string>('')

    const businessKindValue = Form.useWatch('businessKind', form)
    const jobPositionValue = Form.useWatch('jobPosition', form)

    const handleSubmit = (values: SubmissionReqBodyType) => {
        if (!formId) {
            setErrMsg(errorMessage.invalid)
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
            return
        }
        const formattedValues: SubmissionReqBodyType = {
            fullName: values.fullName,
            address: values.address,
            businessKind: values.businessKind,
            jobPosition: values.jobPosition,
            birthPlace: values.birthPlace,
            birthDate: values.birthDate ? dayjs(values.birthDate).format('YYYY-MM-DD') : '',
            ...(values.jobPositionOther && { jobPositionOther: values.jobPositionOther }),
            ...(values.businessKindOther && { businessKindOther: values.businessKindOther }),
            ...(values.email && { email: values.email }),
            ...(values.social_instagram && { social_instagram: values.social_instagram }),
            ...(values.social_facebook && { social_facebook: values.social_facebook }),
            ...(values.social_tiktok && { social_tiktok: values.social_tiktok }),
            ...(values.consent && { consent: values.consent ?? false }),
        }
        const parsedValue = SubmissionReqBodyValidator.safeParse(formattedValues)
        if (parsedValue.error) {
            const errorMessages = parsedValue.error.errors.map(err => err.message).join(', ')
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
            return setErrMsg(`Error Validasi: ${errorMessages}`)
        }
        setLoadingSubmit(true)
        apiHandler<RegisterRes>('POST', apiRoutes.registerSubmit(formId), parsedValue.data)
            .then(res => {
                if (res.error) {
                    if (appErrorMapping[res.code]) {
                        return setStatus(appErrorMapping[res.code])
                    }
                    return setErrMsg(errorMessage.badRequest)
                }

                setStatus(appStatus.completed)
                setErrMsg('')
            })
            .catch(() => {
                setErrMsg(errorMessage.failed)
            })
            .finally(() => {
                setLoadingSubmit(false)
            })
    }

    return (
        <>
            {errorMsg && (
                <Alert
                    message={<p className='danger-color'>{errorMsg}</p>}
                    type='error'
                    closable
                    onClose={() => setErrMsg('')}
                />
            )}

            <div className='welcome-text'>
                <h3>Form Pendaftaran MRS Community</h3>
            </div>

            <Form
                className='form'
                form={form}
                onFinish={handleSubmit}
                layout='vertical'
                scrollToFirstError={true}
                autoComplete='off'
            >
                <InputUi
                    label='Name Lengkap'
                    name='fullName'
                    placeHolder='ex: Nama Lengkap'
                    rules={[
                        { required: true, message: 'Nama lengkap wajib diisi' },
                        { min: 3, message: 'Nama lengkap minimal 3 karakter' },
                    ]}
                />
                <InputUi
                    label='Tempat Tanggal Lahir'
                    name='birthPlace'
                    placeHolder='ex: Jakarta'
                    rules={[
                        { required: true, message: 'Tempat Lahir Wajib diisi' },
                        { min: 3, message: 'Nama lengkap minimal 3 karakter' },
                    ]}
                />
                <DatePickerUi
                    name='birthDate'
                    placeHolder='Tanggal Lahir'
                    formatDate='DD-MM-YYYY'
                    maxCurrentDif={[-18, 'y']}
                    defaultPickerValue={dayjs().subtract(18, 'years')}
                    rules={[{ required: true, message: 'Tanggal Lahir Wajib diisi' }]}
                />
                <SelectUi
                    label='Tipe Usaha'
                    name='businessKind'
                    options={BusinessKindOptions}
                    placeHolder='Pilih tipe usaha'
                    rules={[{ required: true, message: 'Tipe Usaha Wajib dipilih' }]}
                />
                {businessKindValue === 'Other' && (
                    <InputUi
                        name='businessKindOther'
                        placeHolder='ex: tipe usaha lain'
                        rules={[{ required: true, message: 'Wajib diisi' }]}
                    />
                )}
                <SelectUi
                    label='Jabatan'
                    name='jobPosition'
                    options={JobPositionOptions}
                    placeHolder='Pilih jabatan'
                    rules={[{ required: true, message: 'Jabatan Wajib dipilih' }]}
                />
                {jobPositionValue === 'Other' && (
                    <InputUi
                        name='jobPositionOther'
                        placeHolder='ex: jabatan lain'
                        rules={[{ required: true, message: 'Wajib diisi' }]}
                    />
                )}
                <InputUi
                    label='Alamat Rumah'
                    name='address'
                    placeHolder='ex: Nama Lengkap'
                    rules={[{ required: true, message: 'Alamat Wajib diisi' }]}
                />
                <InputUi
                    label='Email'
                    name='email'
                    placeHolder='ex: email@email.com'
                    rules={[
                        {
                            type: 'email',
                            message: 'harap Masukkan format email dengan benar',
                        },
                    ]}
                />
                <InputUi
                    label={'Akun Sosial Media'}
                    name='social_instagram'
                    placeHolder='@username Instagram kamu'
                    prefix={<InstagramFilled />}
                />
                <InputUi
                    name='social_facebook'
                    placeHolder='@username/email Facebook kamu'
                    prefix={<FacebookFilled />}
                />
                <InputUi
                    name='social_tiktok'
                    placeHolder='@username Tiktok kamu'
                    prefix={<TikTokFilled />}
                />
                <CheckBoxGroupUi
                    name='tnc'
                    options={[
                        {
                            label: (
                                <>
                                    Saya menyatakan bahwa informasi yang saya masukkan di atas
                                    adalah benar dan menyetujui{' '}
                                    <a
                                        target='_blank'
                                        href='https://bit.ly/SyaratKetentuanKarapanPointMRS'
                                    >
                                        Syarat & Ketentuan
                                    </a>{' '}
                                    dan{' '}
                                    <a
                                        target='_blank'
                                        href='https://bit.ly/KebijakanPrivasiMRSCommunity'
                                    >
                                        Kebijakan Privasi
                                    </a>{' '}
                                    yang berlaku.
                                </>
                            ),
                            value: true,
                        },
                    ]}
                    rules={[
                        { required: true, message: 'Anda belum menyetujui Syarat & Ketentuan' },
                    ]}
                />
                <CheckBoxUi
                    name='consent'
                    options={[
                        {
                            label: (
                                <>
                                    Saya menyetujui dan memberikan izin untuk menerima informasi
                                    serta penawaran menarik dari MRS Community melalui pesan
                                    singkat, email, dan iklan online yang dipersonalisasi sesuai
                                    dengan minat saya.
                                </>
                            ),
                            value: true,
                        },
                    ]}
                />
                <Form.Item className='m0 mt1'>
                    <Button
                        className='btn-submit'
                        size='large'
                        type='primary'
                        htmlType='submit'
                        loading={loadingSubmit}
                    >
                        Submit
                    </Button>
                </Form.Item>
            </Form>
        </>
    )
}

const App = () => {
    const [searchParams] = useSearchParams()
    const [status, setStatus] = useState<appStatus>(appStatus.init)
    const formId = searchParams.get('token')

    useEffect(() => {
        if (!formId) return setStatus(appStatus.invalid)

        setStatus(appStatus.loading)
        apiHandler<ResProfileCheck>('GET', apiRoutes.checkProfile(formId))
            .then(res => {
                if (res.error) {
                    if (appErrorMapping[res.code]) {
                        return setStatus(appErrorMapping[res.code])
                    }
                    return setStatus(appStatus.invalid)
                }

                setStatus(appStatus.customerInput)
            })
            .catch(() => {
                setStatus(appStatus.error)
            })
    }, [formId])

    return (
        <MainLayout loading={status === appStatus.loading}>
            {(status === appStatus.invalid || status === appStatus.error) && (
                <>
                    <img className='mt1' src={invalidLinkImg} alt='Mrs Logo' />
                    <h2 className='t-center danger-color'>{errorMessage.invalid}</h2>
                </>
            )}
            {status === appStatus.expired && (
                <>
                    <img className='mt1' src={invalidLinkImg} alt='Mrs Logo' />
                    <h2 className='t-center danger-color'>{errorMessage.expired}</h2>
                </>
            )}
            {status === appStatus.used && (
                <>
                    <img className='mt1' src={errorRegisterImg} alt='Mrs Logo' />
                    <h2 className='t-center danger-color'>{errorMessage.formUsed}</h2>
                    <h4 className='t-center balck-color'>
                        Maaf, data Anda sudah terdaftar dalam sistem kami.
                        <br />
                        <br />
                        Jika Anda merasa belum pernah melakukan pendaftaran, atau ada pertanyaan
                        lainnya, silakan hubungi MRS Layanan Pelanggan di nomor 0877-8139-9052.
                        Terima kasih.
                    </h4>
                </>
            )}
            {status === appStatus.completed && (
                <>
                    <img className='mt1 mb1' src={successRegisterImg} alt='Mrs Logo' />
                    <div className='t-center mt-1'>
                        <p className='m0 font-semibold'>
                            Terima kasih! Anda sudah bergabung menjadi Anggota MRS Community.
                        </p>
                        <p className='m0 font-bold'>
                            Silahkan chat kembali dan pilih menu "Cek Poin" untuk melihat
                            <span className='danger-color'> bonus poin </span>
                            Anda
                        </p>
                    </div>
                </>
            )}
            {status === appStatus.customerInput && (
                <RegisterForm formId={formId ?? ''} setStatus={setStatus} />
            )}
        </MainLayout>
    )
}

export default App
